import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import RoleManagement from './RoleManagement';
import UserProfile from './UserProfile';
import PermissionGuard from './PermissionGuard';
import AdminUserCreationModal from './AdminUserCreationModal';
import UserImportModal from './UserImportModal';
import { getLeaderboardData, saveLeaderboardData, clearLeaderboard, getLeaderboard, ACHIEVEMENTS } from '../utils/leaderboardManager';
import { getAdminSettings, saveAdminSettings, getCurrentAdminUser, getSystemStats } from '../utils/adminManager';
import { hasPermission, PERMISSIONS, getUserRole, getRoleById } from '../utils/roleManager';
import { getAllUserAccounts } from '../utils/userManager';

const { 
  FiUsers, FiSettings, FiTrophy, FiTrash2, FiEdit, FiSave, FiX, FiEye, FiEyeOff, 
  FiShield, FiHome, FiDownload, FiUpload, FiClock, FiTarget, FiAward, FiBarChart3, 
  FiUserPlus, FiFileText 
} = FiIcons;

const AdminPanel = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [leaderboardData, setLeaderboardData] = useState({});
  const [adminSettings, setAdminSettings] = useState({});
  const [editingUser, setEditingUser] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [showImportUsers, setShowImportUsers] = useState(false);
  const [systemStats, setSystemStats] = useState({});

  const currentUser = getCurrentAdminUser() || localStorage.getItem('chess_player_name');
  const userRole = getUserRole(currentUser);
  const roleObj = getRoleById(userRole);

  // Debug logging
  useEffect(() => {
    console.log('AdminPanel - Current User:', currentUser);
    console.log('AdminPanel - User Role:', userRole);
    console.log('AdminPanel - Role Object:', roleObj);
    console.log('AdminPanel - Has USER_MANAGEMENT permission:', hasPermission(currentUser, PERMISSIONS.USER_MANAGEMENT));
  }, [currentUser, userRole, roleObj]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = () => {
    setLeaderboardData(getLeaderboardData());
    setAdminSettings(getAdminSettings());
    setSystemStats(getSystemStats());
  };

  const handleUpdateUser = (username, updatedStats) => {
    // Check permission
    if (!hasPermission(currentUser, PERMISSIONS.USER_MANAGEMENT)) {
      alert('You do not have permission to modify users');
      return;
    }

    const data = { ...leaderboardData };
    data[username] = { ...data[username], ...updatedStats };
    setLeaderboardData(data);
    saveLeaderboardData(data);
    setEditingUser(null);
    loadData(); // Refresh stats
  };

  const handleDeleteUser = (username) => {
    // Check permission
    if (!hasPermission(currentUser, PERMISSIONS.USER_MANAGEMENT)) {
      alert('You do not have permission to delete users');
      return;
    }

    const data = { ...leaderboardData };
    delete data[username];
    setLeaderboardData(data);
    saveLeaderboardData(data);
    setShowDeleteConfirm(null);
    loadData(); // Refresh stats
  };

  const handleSettingsUpdate = (newSettings) => {
    // Check permission
    if (!hasPermission(currentUser, PERMISSIONS.SYSTEM_SETTINGS)) {
      alert('You do not have permission to modify system settings');
      return;
    }

    setAdminSettings(newSettings);
    saveAdminSettings(newSettings);
  };

  const exportAllData = () => {
    // Check permission
    if (!hasPermission(currentUser, PERMISSIONS.EXPORT_DATA)) {
      alert('You do not have permission to export data');
      return;
    }

    const allData = {
      leaderboard: leaderboardData,
      settings: adminSettings,
      roles: JSON.parse(localStorage.getItem('chess_user_roles') || '{}'),
      userAccounts: getAllUserAccounts(),
      exportDate: new Date().toISOString(),
      exportedBy: currentUser
    };

    const dataStr = JSON.stringify(allData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(dataBlob);
    link.download = `chess_admin_backup_${new Date().toISOString().split('T')[0]}.json`;
    link.click();
  };

  const handleImportData = (event) => {
    // Check permission
    if (!hasPermission(currentUser, PERMISSIONS.SYSTEM_SETTINGS)) {
      alert('You do not have permission to import data');
      return;
    }

    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const importedData = JSON.parse(e.target.result);
          
          if (importedData.leaderboard) {
            setLeaderboardData(importedData.leaderboard);
            saveLeaderboardData(importedData.leaderboard);
          }
          
          if (importedData.settings) {
            setAdminSettings(importedData.settings);
            saveAdminSettings(importedData.settings);
          }
          
          if (importedData.roles) {
            localStorage.setItem('chess_user_roles', JSON.stringify(importedData.roles));
          }
          
          if (importedData.userAccounts) {
            localStorage.setItem('chess_user_accounts', JSON.stringify(importedData.userAccounts));
          }
          
          alert('Data imported successfully!');
          loadData(); // Refresh all data
        } catch (error) {
          alert('Error importing data. Please check the file format.');
        }
      };
      reader.readAsText(file);
    }
  };

  const handleUserCreated = (newUser) => {
    loadData(); // Refresh data after user creation
    alert(`User "${newUser.username}" created successfully!`);
  };

  const handleUsersImported = (results) => {
    loadData(); // Refresh data after import
    alert(`Successfully imported ${results.success} users!`);
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: FiBarChart3, permission: PERMISSIONS.VIEW_ANALYTICS },
    { id: 'users', label: 'User Management', icon: FiUsers, permission: PERMISSIONS.USER_MANAGEMENT },
    { id: 'roles', label: 'Role Management', icon: FiShield, permission: PERMISSIONS.USER_MANAGEMENT },
    { id: 'leaderboard', label: 'Leaderboard', icon: FiTrophy, permission: PERMISSIONS.LEADERBOARD_MANAGEMENT },
    { id: 'settings', label: 'System Settings', icon: FiSettings, permission: PERMISSIONS.SYSTEM_SETTINGS }
  ].filter(tab => hasPermission(currentUser, tab.permission));

  return (
    <div className="min-h-screen p-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between mb-8"
        >
          <div className="flex items-center gap-3">
            <SafeIcon icon={FiShield} className="text-4xl text-red-400" />
            <div>
              <h1 className="text-4xl font-bold text-white">Admin Panel</h1>
              <p className="text-gray-300">
                Welcome, {currentUser} • {roleObj?.name || 'User'}
              </p>
            </div>
          </div>

          <div className="flex gap-3">
            <PermissionGuard permission={PERMISSIONS.EXPORT_DATA}>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={exportAllData}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold transition-colors"
              >
                <SafeIcon icon={FiDownload} />
                Export Data
              </motion.button>
            </PermissionGuard>

            <PermissionGuard permission={PERMISSIONS.SYSTEM_SETTINGS}>
              <label className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-colors cursor-pointer">
                <SafeIcon icon={FiUpload} />
                Import Data
                <input
                  type="file"
                  accept=".json"
                  onChange={handleImportData}
                  className="hidden"
                />
              </label>
            </PermissionGuard>

            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate('/')}
              className="flex items-center gap-2 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-semibold transition-colors"
            >
              <SafeIcon icon={FiHome} />
              Exit Admin
            </motion.button>
          </div>
        </motion.div>

        {/* Tab Navigation */}
        <div className="flex space-x-1 bg-white/10 p-1 rounded-xl mb-8 overflow-x-auto">
          {tabs.map((tab) => (
            <motion.button
              key={tab.id}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition-all whitespace-nowrap ${
                activeTab === tab.id 
                  ? 'bg-white text-gray-900' 
                  : 'text-white hover:bg-white/10'
              }`}
            >
              <SafeIcon icon={tab.icon} />
              {tab.label}
            </motion.button>
          ))}
        </div>

        {/* Tab Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.2 }}
          >
            {activeTab === 'overview' && (
              <OverviewTab
                systemStats={systemStats}
                currentUser={currentUser}
              />
            )}

            {activeTab === 'users' && (
              <UserManagementTab
                leaderboardData={leaderboardData}
                editingUser={editingUser}
                setEditingUser={setEditingUser}
                onUpdateUser={handleUpdateUser}
                onDeleteUser={handleDeleteUser}
                showDeleteConfirm={showDeleteConfirm}
                setShowDeleteConfirm={setShowDeleteConfirm}
                currentUser={currentUser}
                onCreateUser={() => setShowCreateUser(true)}
                onImportUsers={() => setShowImportUsers(true)}
              />
            )}

            {activeTab === 'roles' && (
              <RoleManagement currentUser={currentUser} />
            )}

            {activeTab === 'leaderboard' && (
              <LeaderboardManagementTab
                leaderboardData={leaderboardData}
                onClearLeaderboard={() => {
                  if (window.confirm('Are you sure you want to clear the entire leaderboard? This cannot be undone.')) {
                    clearLeaderboard();
                    setLeaderboardData({});
                    loadData();
                  }
                }}
                currentUser={currentUser}
              />
            )}

            {activeTab === 'settings' && (
              <SystemSettingsTab
                settings={adminSettings}
                onUpdateSettings={handleSettingsUpdate}
                currentUser={currentUser}
              />
            )}
          </motion.div>
        </AnimatePresence>

        {/* Create User Modal */}
        <AdminUserCreationModal
          isOpen={showCreateUser}
          onClose={() => setShowCreateUser(false)}
          onSuccess={handleUserCreated}
          currentUser={currentUser}
        />

        {/* Import Users Modal */}
        <UserImportModal
          isOpen={showImportUsers}
          onClose={() => setShowImportUsers(false)}
          onSuccess={handleUsersImported}
          currentUser={currentUser}
        />
      </div>
    </div>
  );
};

// Overview Tab Component (unchanged)
const OverviewTab = ({ systemStats, currentUser }) => {
  const userRole = getUserRole(currentUser);
  const roleObj = getRoleById(userRole);

  return (
    <div className="space-y-6">
      {/* User Profile */}
      <UserProfile username={currentUser} />

      {/* System Statistics */}
      <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
        <h2 className="text-2xl font-bold text-white mb-6">System Overview</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          <div className="bg-blue-500/20 border border-blue-500/50 rounded-lg p-4">
            <div className="text-2xl font-bold text-blue-400">{systemStats.totalUsers || 0}</div>
            <div className="text-blue-300">Total Users</div>
          </div>
          <div className="bg-green-500/20 border border-green-500/50 rounded-lg p-4">
            <div className="text-2xl font-bold text-green-400">{systemStats.totalGames || 0}</div>
            <div className="text-green-300">Total Games</div>
          </div>
          <div className="bg-purple-500/20 border border-purple-500/50 rounded-lg p-4">
            <div className="text-2xl font-bold text-purple-400">{systemStats.averageRating || 1200}</div>
            <div className="text-purple-300">Average Rating</div>
          </div>
          <div className="bg-orange-500/20 border border-orange-500/50 rounded-lg p-4">
            <div className="text-2xl font-bold text-orange-400">{systemStats.activeUsers || 0}</div>
            <div className="text-orange-300">Active Users (7d)</div>
          </div>
        </div>

        {/* Role Distribution */}
        {systemStats.roleDistribution && (
          <div>
            <h3 className="text-lg font-semibold text-white mb-4">Role Distribution</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {Object.entries(systemStats.roleDistribution).map(([roleId, count]) => {
                const role = getRoleById(roleId);
                return (
                  <div key={roleId} className="bg-white/5 rounded-lg p-3">
                    <div className="text-lg font-bold text-white">{count}</div>
                    <div className="text-gray-300 text-sm">{role?.name || roleId}</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// User Management Tab Component (with debug logging)
const UserManagementTab = ({
  leaderboardData,
  editingUser,
  setEditingUser,
  onUpdateUser,
  onDeleteUser,
  showDeleteConfirm,
  setShowDeleteConfirm,
  currentUser,
  onCreateUser,
  onImportUsers
}) => {
  const users = Object.entries(leaderboardData).map(([name, stats]) => ({ name, ...stats }));

  // Debug logging for permissions
  const hasUserManagement = hasPermission(currentUser, PERMISSIONS.USER_MANAGEMENT);
  console.log('UserManagementTab - Current User:', currentUser);
  console.log('UserManagementTab - Has USER_MANAGEMENT permission:', hasUserManagement);
  console.log('UserManagementTab - PERMISSIONS object:', PERMISSIONS);

  const canModifyUser = (targetUsername) => {
    if (targetUsername === currentUser) return false; // Can't modify self
    const currentUserRole = getUserRole(currentUser);
    const currentUserRoleObj = getRoleById(currentUserRole);
    const targetUserRole = getUserRole(targetUsername);
    const targetUserRoleObj = getRoleById(targetUserRole);

    // Can only modify users with lower role level
    return currentUserRoleObj.level > targetUserRoleObj.level;
  };

  return (
    <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-white">User Management</h2>
        
        <div className="flex gap-3">
          {hasUserManagement && (
            <>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={onImportUsers}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-colors"
              >
                <SafeIcon icon={FiFileText} />
                Import Users
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={onCreateUser}
                className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-semibold transition-colors"
              >
                <SafeIcon icon={FiUserPlus} />
                Create User
              </motion.button>
            </>
          )}
          
          {!hasUserManagement && (
            <div className="text-gray-400 text-sm">
              Insufficient permissions for user management
            </div>
          )}
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-left">
          <thead>
            <tr className="border-b border-white/20">
              <th className="pb-3 text-white font-semibold">Username</th>
              <th className="pb-3 text-white font-semibold">Role</th>
              <th className="pb-3 text-white font-semibold">Rating</th>
              <th className="pb-3 text-white font-semibold">Games</th>
              <th className="pb-3 text-white font-semibold">Win Rate</th>
              <th className="pb-3 text-white font-semibold">Last Played</th>
              <th className="pb-3 text-white font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => {
              const userRole = getUserRole(user.name);
              const roleObj = getRoleById(userRole);
              return (
                <tr key={user.name} className="border-b border-white/10">
                  <td className="py-3 text-white font-medium">
                    {user.name}
                    {user.name === currentUser && (
                      <span className="text-blue-400 text-sm ml-2">(You)</span>
                    )}
                  </td>
                  <td className="py-3">
                    <span className="px-2 py-1 bg-blue-500/20 text-blue-300 rounded text-sm">
                      {roleObj?.name || 'User'}
                    </span>
                  </td>
                  <td className="py-3 text-yellow-400">{user.rating}</td>
                  <td className="py-3 text-gray-300">{user.gamesPlayed}</td>
                  <td className="py-3 text-green-400">{user.winRate.toFixed(1)}%</td>
                  <td className="py-3 text-gray-300">
                    {user.lastPlayed ? new Date(user.lastPlayed).toLocaleDateString() : 'Never'}
                  </td>
                  <td className="py-3">
                    {canModifyUser(user.name) && hasUserManagement ? (
                      <div className="flex gap-2">
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => setEditingUser(user)}
                          className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm font-semibold transition-colors"
                        >
                          <SafeIcon icon={FiEdit} />
                        </motion.button>
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => setShowDeleteConfirm(user.name)}
                          className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded text-sm font-semibold transition-colors"
                        >
                          <SafeIcon icon={FiTrash2} />
                        </motion.button>
                      </div>
                    ) : (
                      <span className="text-gray-500 text-sm">Protected</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Edit User Modal */}
      {editingUser && (
        <EditUserModal
          user={editingUser}
          onSave={onUpdateUser}
          onClose={() => setEditingUser(null)}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <DeleteConfirmModal
          username={showDeleteConfirm}
          onConfirm={() => onDeleteUser(showDeleteConfirm)}
          onCancel={() => setShowDeleteConfirm(null)}
        />
      )}
    </div>
  );
};

// Edit User Modal Component (unchanged)
const EditUserModal = ({ user, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    rating: user.rating,
    wins: user.wins,
    losses: user.losses,
    draws: user.draws,
    gamesPlayed: user.gamesPlayed
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const updatedStats = {
      ...formData,
      winRate: formData.gamesPlayed > 0 ? (formData.wins / formData.gamesPlayed) * 100 : 0
    };
    onSave(user.name, updatedStats);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50"
    >
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="bg-gradient-to-br from-slate-800 to-slate-900 p-6 rounded-2xl shadow-2xl border border-white/20 max-w-md w-full"
      >
        <h3 className="text-xl font-bold text-white mb-4">Edit User: {user.name}</h3>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Rating</label>
            <input
              type="number"
              value={formData.rating}
              onChange={(e) => setFormData({ ...formData, rating: parseInt(e.target.value) })}
              className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white"
            />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Wins</label>
              <input
                type="number"
                value={formData.wins}
                onChange={(e) => setFormData({ ...formData, wins: parseInt(e.target.value) })}
                className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Losses</label>
              <input
                type="number"
                value={formData.losses}
                onChange={(e) => setFormData({ ...formData, losses: parseInt(e.target.value) })}
                className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Draws</label>
              <input
                type="number"
                value={formData.draws}
                onChange={(e) => setFormData({ ...formData, draws: parseInt(e.target.value) })}
                className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white"
              />
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              type="submit"
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold transition-colors"
            >
              <SafeIcon icon={FiSave} />
              Save Changes
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              type="button"
              onClick={onClose}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-semibold transition-colors"
            >
              <SafeIcon icon={FiX} />
              Cancel
            </motion.button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

// Delete Confirmation Modal Component (unchanged)
const DeleteConfirmModal = ({ username, onConfirm, onCancel }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50"
  >
    <motion.div
      initial={{ scale: 0.8, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      className="bg-gradient-to-br from-red-800 to-red-900 p-6 rounded-2xl shadow-2xl border border-red-500/20 max-w-md w-full"
    >
      <h3 className="text-xl font-bold text-white mb-4">Delete User</h3>
      <p className="text-red-100 mb-6">
        Are you sure you want to delete user "{username}"? This action cannot be undone.
      </p>
      <div className="flex gap-3">
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={onConfirm}
          className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold transition-colors"
        >
          Delete
        </motion.button>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={onCancel}
          className="flex-1 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-semibold transition-colors"
        >
          Cancel
        </motion.button>
      </div>
    </motion.div>
  </motion.div>
);

// Leaderboard Management Tab Component (updated with permission checking)
const LeaderboardManagementTab = ({ leaderboardData, onClearLeaderboard, currentUser }) => {
  const leaderboard = getLeaderboard('rating', 50);
  const canClearLeaderboard = hasPermission(currentUser, PERMISSIONS.LEADERBOARD_MANAGEMENT);

  return (
    <div className="space-y-6">
      <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-white">Leaderboard Management</h2>
          {canClearLeaderboard && (
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={onClearLeaderboard}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold transition-colors"
            >
              <SafeIcon icon={FiTrash2} />
              Clear All Data
            </motion.button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="bg-blue-500/20 border border-blue-500/50 rounded-lg p-4">
            <div className="text-2xl font-bold text-blue-400">{Object.keys(leaderboardData).length}</div>
            <div className="text-blue-300">Total Users</div>
          </div>
          <div className="bg-green-500/20 border border-green-500/50 rounded-lg p-4">
            <div className="text-2xl font-bold text-green-400">
              {Object.values(leaderboardData).reduce((sum, user) => sum + user.gamesPlayed, 0)}
            </div>
            <div className="text-green-300">Total Games</div>
          </div>
          <div className="bg-purple-500/20 border border-purple-500/50 rounded-lg p-4">
            <div className="text-2xl font-bold text-purple-400">
              {Math.round(Object.values(leaderboardData).reduce((sum, user) => sum + user.rating, 0) / Object.keys(leaderboardData).length) || 0}
            </div>
            <div className="text-purple-300">Average Rating</div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-white/20">
                <th className="pb-3 text-white font-semibold">Rank</th>
                <th className="pb-3 text-white font-semibold">Player</th>
                <th className="pb-3 text-white font-semibold">Role</th>
                <th className="pb-3 text-white font-semibold">Rating</th>
                <th className="pb-3 text-white font-semibold">Games</th>
                <th className="pb-3 text-white font-semibold">W-L-D</th>
                <th className="pb-3 text-white font-semibold">Win Rate</th>
              </tr>
            </thead>
            <tbody>
              {leaderboard.map((player, index) => {
                const userRole = getUserRole(player.name);
                const roleObj = getRoleById(userRole);
                return (
                  <tr key={player.name} className="border-b border-white/10">
                    <td className="py-3 text-yellow-400 font-bold">#{index + 1}</td>
                    <td className="py-3 text-white font-medium">{player.name}</td>
                    <td className="py-3">
                      <span className="px-2 py-1 bg-blue-500/20 text-blue-300 rounded text-xs">
                        {roleObj?.name || 'User'}
                      </span>
                    </td>
                    <td className="py-3 text-yellow-400">{player.rating}</td>
                    <td className="py-3 text-gray-300">{player.gamesPlayed}</td>
                    <td className="py-3 text-gray-300">{player.wins}-{player.losses}-{player.draws}</td>
                    <td className="py-3 text-green-400">{player.winRate.toFixed(1)}%</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// System Settings Tab Component (updated with permission checking)
const SystemSettingsTab = ({ settings, onUpdateSettings, currentUser }) => {
  const [formData, setFormData] = useState({
    gameTimeLimit: settings.gameTimeLimit || 120,
    maxGamesPerDay: settings.maxGamesPerDay || 50,
    enableTutorial: settings.enableTutorial !== false,
    enableLeaderboard: settings.enableLeaderboard !== false,
    maintenanceMode: settings.maintenanceMode || false,
    welcomeMessage: settings.welcomeMessage || 'Welcome to Chess Master!',
    roleBasedAccess: settings.roleBasedAccess !== false,
    sessionTimeout: settings.sessionTimeout || 24,
    ...settings
  });

  const canModifySettings = hasPermission(currentUser, PERMISSIONS.SYSTEM_SETTINGS);

  const handleSave = () => {
    if (!canModifySettings) {
      alert('You do not have permission to modify system settings');
      return;
    }

    onUpdateSettings(formData);
    alert('Settings saved successfully!');
  };

  return (
    <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
      <h2 className="text-2xl font-bold text-white mb-6">System Settings</h2>

      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Game Time Limit (seconds)</label>
            <input
              type="number"
              value={formData.gameTimeLimit}
              onChange={(e) => setFormData({ ...formData, gameTimeLimit: parseInt(e.target.value) })}
              disabled={!canModifySettings}
              className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white disabled:opacity-50"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Max Games Per Day</label>
            <input
              type="number"
              value={formData.maxGamesPerDay}
              onChange={(e) => setFormData({ ...formData, maxGamesPerDay: parseInt(e.target.value) })}
              disabled={!canModifySettings}
              className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white disabled:opacity-50"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Session Timeout (hours)</label>
            <input
              type="number"
              value={formData.sessionTimeout}
              onChange={(e) => setFormData({ ...formData, sessionTimeout: parseInt(e.target.value) })}
              disabled={!canModifySettings}
              className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white disabled:opacity-50"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">Welcome Message</label>
          <textarea
            value={formData.welcomeMessage}
            onChange={(e) => setFormData({ ...formData, welcomeMessage: e.target.value })}
            disabled={!canModifySettings}
            className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white h-20 disabled:opacity-50"
            placeholder="Enter welcome message..."
          />
        </div>

        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-white">Feature Toggles</h3>
          <div className="space-y-3">
            <label className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={formData.enableTutorial}
                onChange={(e) => setFormData({ ...formData, enableTutorial: e.target.checked })}
                disabled={!canModifySettings}
                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-50"
              />
              <span className="text-white">Enable Tutorial System</span>
            </label>

            <label className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={formData.enableLeaderboard}
                onChange={(e) => setFormData({ ...formData, enableLeaderboard: e.target.checked })}
                disabled={!canModifySettings}
                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-50"
              />
              <span className="text-white">Enable Leaderboard</span>
            </label>

            <label className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={formData.roleBasedAccess}
                onChange={(e) => setFormData({ ...formData, roleBasedAccess: e.target.checked })}
                disabled={!canModifySettings}
                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-50"
              />
              <span className="text-white">Enable Role-Based Access Control</span>
            </label>

            <label className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={formData.maintenanceMode}
                onChange={(e) => setFormData({ ...formData, maintenanceMode: e.target.checked })}
                disabled={!canModifySettings}
                className="w-4 h-4 text-red-600 bg-gray-100 border-gray-300 rounded focus:ring-red-500 disabled:opacity-50"
              />
              <span className="text-red-400">Maintenance Mode</span>
            </label>
          </div>
        </div>

        {canModifySettings && (
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleSave}
            className="flex items-center gap-2 px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold transition-colors"
          >
            <SafeIcon icon={FiSave} />
            Save Settings
          </motion.button>
        )}
      </div>
    </div>
  );
};

export default AdminPanel;